import type { TimeComponent } from "./types";

// get week number
// export toWeekNumber() {
// }

export type SettingsZ = {
	Z?: boolean;
};

export type Settings = SettingsZ & {
	add?: boolean;
	overflowMM?: boolean;
	epoch?: number;
	YYYY?: string | number;
	MM?: string | number;
	DD?: string | number;
	T?: string | 0;
	HH?: string | number;
	mm?: string | number;
	ss?: string | number;
	sss?: string | number;
};

export const timeTo = (
  init?:
  | TimeComponent
  | { component: TimeComponent; UTC?: boolean }
	// | Intl.DateTimeFormatOptions // would require getting the keys and recursive for more components
  | Date,
  date?: Date
): string => {
  const d = init instanceof Date ? init : date || new Date();
	const { component: c, UTC: Z } = !(init instanceof Date) && typeof init === "object" ?
    init : { component: typeof init === "string" ? init : "", UTC: false };
  const lang = "en-US";
  const timeDefaults = { hour12: false, ...(Z ? { timeZone: "UTC" } : {}) }; // "Europe/Oslo"
  const timeLocale = (values: Intl.DateTimeFormatOptions, d: Date) =>
    new Intl.DateTimeFormat(lang, {
      ...timeDefaults,
      ...values
    }).format(d);

	if (/^year|YYYY$/.test(c)) return timeLocale({ year: "numeric" }, d);
	if (/^month|MM$/.test(c)) return timeLocale({ month: "2-digit" }, d);
	if (/^day|DD$/.test(c)) return timeLocale({ day: "2-digit" }, d);
	if (/^hour|HH$/.test(c)) return timeLocale({ hour: "2-digit" }, d);
	if (/^minute|mm$/.test(c)) return timeLocale({ minute: "2-digit" }, d);
	if (/^second|ss$/.test(c)) return timeLocale({ second: "2-digit" }, d);
	if (/^millisecond|sss$/.test(c)) return timeLocale({ fractionalSecondDigits: 3 }, d);
  // else (if "epoch" or undefined)
	return String(d.getTime());
};

/**
 * Sets and returns a new date from `d` Date object.
 * @param opt: Settings - props for how to set Date
 * @param d: Date - pass a Date object, if not, `new Date()` (default)
 * @param returnNew: boolean - `true` returns a new Date object (default), or pass `false` to mutate `d`
 * @returns Date
 */
export const setNewDate = (opt: Settings = {}, d?: Date, returnNew = true): Date => {
	const D = !d ? new Date() : returnNew && d ? new Date(d) : d;
	const setMonthByOverflow = (i: number) => {
		if (opt?.overflowMM) (() => opt.add || !opt.Z ? D.setMonth(i) : D.setUTCMonth(i))();
		if (opt?.overflowMM) return;
		const dayUTC = D.getUTCDate();
		const day = opt.add || !opt.Z ? D.getDate() : dayUTC;
		(() => opt.add || !opt.Z ? D.setDate(1) : D.setUTCDate(1))();
		(() => opt.add || !opt.Z ? D.setMonth(i) : D.setUTCMonth(i))();
		const endOfMonthDate = new Date();
		endOfMonthDate.setUTCFullYear(opt.add || !opt.Z ? D.getFullYear() : D.getUTCFullYear(), i + 1, 0);
		const endOfMonthDay = endOfMonthDate.getUTCDate();
		if (day > endOfMonthDay) (() => opt.add || !opt.Z ? D.setDate(endOfMonthDay) : D.setUTCDate(endOfMonthDay))();
		else (() => opt.add || !opt.Z ? D.setDate(day) : D.setUTCDate(day))();
	};

	// Return if no options
	if (Object.keys(opt).length === 0) return D;
	// Set by `epoch`
	if (typeof opt.epoch === "number" && !opt.add) D.setTime(opt.epoch);
	else if (typeof opt.epoch === "number") D.setTime(D.getTime() + opt.epoch);
	// Set by `YYYY`, `MM`, `DD`
	if (typeof opt.YYYY !== "undefined" && typeof +opt.YYYY === "number") {
		const y = opt.add ? D.getFullYear() + +opt.YYYY : +opt.YYYY;
		(() => opt.add || !opt.Z ? D.setFullYear(y) : D.setUTCFullYear(y))();
	}
	if (typeof opt.MM !== "undefined" && typeof +opt.MM === "number")
		setMonthByOverflow(opt.add ? D.getMonth() + +opt.MM : +opt.MM - 1);
	if (typeof opt.DD !== "undefined" && typeof +opt.DD === "number") {
		const d = opt.add ? D.getDate() + +opt.DD : +opt.DD;
		(() => opt.add || !opt.Z ? D.setDate(d) : D.setUTCDate(d))();
	}
	// Set by `T`, ISO time string
	if (typeof opt.T === "string" || opt.T === 0) {
		// Remove prepending "T" and appending timezone, if any
		const timeOnly = `${opt.T}`.replace(/T?(.*)(Z$|[-+].*)/g, "$1");
		const [hh, mm, sssss] = timeOnly.split(":");
		const [ss, sss] = (sssss || "").split(".");
		// Set hours, minute, second, millisecond
		(() => typeof hh === "string" && /^\d\d?$/.test(hh) ? opt.Z ?
			D.setUTCHours(+hh) : D.setHours(+hh) : D.setHours(0))();
		(() => typeof mm === "string" && /^\d\d?$/.test(mm) ? opt.Z ?
			D.setUTCMinutes(+mm) : D.setMinutes(+mm) : D.setMinutes(0))();
		(() => typeof ss === "string" && /^\d\d?$/.test(ss) ? opt.Z ?
			D.setUTCSeconds(+ss) : D.setSeconds(+ss) : D.setSeconds(0))();
		(() => typeof sss === "string" && /^\d\d?\d?$/.test(sss) ? opt.Z ?
			D.setUTCMilliseconds(+sss) : D.setMilliseconds(+sss) : D.setMilliseconds(0))();
	}
	// Set hours, minutes, seconds, milliseconds
	if (typeof opt.HH !== "undefined" && /^-?\d\d?$/.test(`${opt.HH}`)) {
		const h = opt.add ? D.getUTCHours() + +opt.HH : +opt.HH;
		(() => opt.Z || opt.add ? D.setUTCHours(h) : D.setHours(h))();
	}
	if (typeof opt.mm !== "undefined" && /^-?\d\d?$/.test(`${opt.mm}`)) {
		const m = opt.add ? D.getUTCMinutes() + +opt.mm : +opt.mm;
		(() => opt.Z || opt.add ? D.setUTCMinutes(m) : D.setMinutes(m))();
	}
	if (typeof opt.ss !== "undefined" && typeof (+opt.ss) === "number") {
		const s = opt.add ? D.getUTCSeconds() + +opt.ss : +opt.ss;
		(() => opt.Z || opt.add ? D.setUTCSeconds(s) : D.setSeconds(s))();
	}
	if (typeof opt.sss !== "undefined" && typeof (+opt.sss) === "number") {
		const ms = opt.add ? D.getUTCMilliseconds() + +opt.sss : +opt.sss;
		(() => opt.Z || opt.add ? D.setUTCMilliseconds(ms) : D.setMilliseconds(ms))();
	}

	return D;
};

export const dateGet = (
	init?: TimeComponent | { UTC?: boolean, component?: TimeComponent; },
	D = new Date()
): string => {
	const { component: c, UTC: Z } = typeof init === "object" ? init : { component: init, UTC: false };

	if (/^year|YYYY$/.test(`${c || ""}`))
		return String(Z ? D.getUTCFullYear() : D.getFullYear()).padStart(4, "0");
	if (/^month|MM$/.test(`${c || ""}`))
		return String((Z ? D.getUTCMonth() : D.getMonth()) + 1).padStart(2, "0");
	if (/^day|DD$/.test(`${c || ""}`))
		return String(Z ? D.getUTCDate() : D.getDate()).padStart(2, "0");
	if (/^hour|HH$/.test(`${c || ""}`))
		return String(Z ? D.getUTCHours() : D.getHours()).padStart(2, "0");
	if (/^minute|mm$/.test(`${c || ""}`))
		return String(Z ? D.getUTCMinutes() : D.getMinutes()).padStart(2, "0");
	if (/^second|ss$/.test(`${c || ""}`))
		return String(Z ? D.getUTCSeconds() : D.getSeconds()).padStart(2, "0");
	if (/^millisecond|sss$/.test(`${c || ""}`))
		return String(Z ? D.getUTCMilliseconds() : D.getMilliseconds()).padStart(3, "0");
	else // if "epoch" or undefined
		return String(D.getTime());
};

export const dateGetUTC = (c?: TimeComponent, D = new Date()): string =>
	dateGet({
			...(c ? { component: c } : { component: "epoch" }),
			UTC: true
		}, D
	);
